(function () {
  const SEARCH_TAG = 'tag:for-search';
  const DEBOUNCE_MS = 300;
  const SEARCH_SYNONYM_MAP = {
    mouse: 'rodent',
    mice: 'rodent',
    rodents: 'rodent',
  };

  function stripSearchTag(query) {
    return (query || '').replace(new RegExp(SEARCH_TAG, 'gi'), '').trim();
  }

  function normalizeSearchTerm(term) {
    return stripSearchTag(term).toLowerCase();
  }

  function expandSearchQuery(query) {
    const cleaned = stripSearchTag(query);
    if (!cleaned) return cleaned;

    const normalized = normalizeSearchTerm(cleaned);

    if (SEARCH_SYNONYM_MAP[normalized]) {
      return SEARCH_SYNONYM_MAP[normalized];
    }

    const synonymEntries = Object.entries(SEARCH_SYNONYM_MAP).sort(
      (a, b) => b[0].length - a[0].length
    );

    for (const [term, replacement] of synonymEntries) {
      if (normalized.length >= 3 && term.startsWith(normalized)) {
        return replacement;
      }
    }

    return cleaned;
  }

  function appendSearchTag(query) {
    const expanded = expandSearchQuery(stripSearchTag(query));
    if (!expanded) return SEARCH_TAG;
    if (expanded.toLowerCase().includes(SEARCH_TAG)) return expanded;
    return `${expanded} ${SEARCH_TAG}`;
  }

  function getPreviewThemeId() {
    const fromUrl = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (fromUrl) return fromUrl;

    const cookieMatch = document.cookie.match(/(?:^|;\s*)preview_theme=(\d+)/);
    return cookieMatch ? cookieMatch[1] : null;
  }

  function getPredictiveSearchUrl() {
    return (window.routes && window.routes.predictive_search_url) || '/search/suggest';
  }

  function buildPredictiveSearchUrl(query) {
    const params = new URLSearchParams({
      q: appendSearchTag(query),
      section_id: 'predictive-search',
    });

    const previewThemeId = getPreviewThemeId();
    if (previewThemeId) {
      params.set('preview_theme_id', previewThemeId);
    }

    return `${getPredictiveSearchUrl()}?${params.toString()}`;
  }

  function appendTagToSearchUrl(url) {
    if (!url) return url;

    const isPredictiveSearch =
      url.includes('/search/suggest') || url.includes('section_id=predictive-search');

    if (!isPredictiveSearch) return url;

    const parsed = new URL(url, window.location.origin);
    const rawQuery = stripSearchTag(parsed.searchParams.get('q') || '');
    parsed.searchParams.set('q', appendSearchTag(rawQuery));
    parsed.searchParams.delete('resources[type]');
    parsed.searchParams.delete('resources[limit]');

    if (!parsed.searchParams.get('section_id')) {
      parsed.searchParams.set('section_id', 'predictive-search');
    }

    const previewThemeId = getPreviewThemeId();
    if (previewThemeId && !parsed.searchParams.get('preview_theme_id')) {
      parsed.searchParams.set('preview_theme_id', previewThemeId);
    }

    return parsed.pathname + parsed.search;
  }

  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    let url = typeof input === 'string' ? input : input instanceof Request ? input.url : null;

    if (url) {
      const updatedUrl = appendTagToSearchUrl(url);
      if (updatedUrl !== url) {
        input = typeof input === 'string' ? updatedUrl : new Request(updatedUrl, input);
      }
    }

    return originalFetch.call(this, input, init);
  };

  function bindSearchForms() {
    document.querySelectorAll('form[role="search"]').forEach((form) => {
      if (form.dataset.searchTagBound === 'true') return;
      form.dataset.searchTagBound = 'true';

      form.addEventListener('submit', () => {
        const input = form.querySelector('input[name="q"]');
        if (input) input.value = appendSearchTag(stripSearchTag(input.value));
      });
    });
  }

  function normalizeSearchResultsUrl() {
    if (!window.location.pathname.endsWith('/search')) return;

    const params = new URLSearchParams(window.location.search);
    const query = params.get('q');

    if (!query || query.toLowerCase().includes(SEARCH_TAG)) return;

    params.set('q', appendSearchTag(stripSearchTag(query)));
    window.location.replace(`${window.location.pathname}?${params.toString()}`);
  }

  function getLoadingMarkup() {
    return (
      '<div class="predictive-search__loading-state" aria-hidden="true">' +
      '<svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
      '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
      '</svg></div>'
    );
  }

  function renderPredictiveMarkup(predictiveSearch, resultsContainer, html) {
    if (html.includes('Invalid parameter error')) {
      throw new Error(html.trim());
    }

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const section = parsed.querySelector('#shopify-section-predictive-search');

    if (!section) {
      throw new Error('Predictive search section missing from response');
    }

    resultsContainer.innerHTML = section.innerHTML;
    predictiveSearch.setAttribute('results', 'true');
    predictiveSearch.setAttribute('open', 'true');
    predictiveSearch.removeAttribute('loading');

    const input = predictiveSearch.querySelector('input[name="q"]');
    if (input) input.setAttribute('aria-expanded', 'true');
  }

  function updateSearchForDisplay(predictiveSearch, displayTerm) {
    const textEl = predictiveSearch.querySelector('[data-predictive-search-search-for-text]');
    if (!textEl || !displayTerm) return;

    const current = textEl.textContent || '';
    const quoted = current.match(/[“"](.+?)[”"]/);
    if (quoted) {
      textEl.textContent = current.replace(quoted[0], `"${displayTerm}"`);
    }
  }

  function showPredictiveFallback(predictiveSearch, resultsContainer, displayTerm) {
    const safeTerm = (displayTerm || '').replace(/"/g, '');
    resultsContainer.innerHTML =
      '<div id="predictive-search-results" role="listbox">' +
      getLoadingMarkup() +
      '<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">' +
      '<button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">' +
      `<span data-predictive-search-search-for-text">Search for "${safeTerm}"</span>` +
      '</button></div></div>';

    predictiveSearch.setAttribute('open', 'true');
    predictiveSearch.removeAttribute('loading');
    predictiveSearch.setAttribute('results', 'true');

    const input = predictiveSearch.querySelector('input[name="q"]');
    if (input) input.setAttribute('aria-expanded', 'true');
  }

  function initHeaderModalPredictiveSearch() {
    document.querySelectorAll('.header__search predictive-search').forEach((predictiveSearch) => {
      if (predictiveSearch.dataset.headerPredictiveBound === 'true') return;
      predictiveSearch.dataset.headerPredictiveBound = 'true';

      const input = predictiveSearch.querySelector('input[name="q"]');
      const resultsContainer = predictiveSearch.querySelector('[data-predictive-search]');
      if (!input || !resultsContainer) return;

      let debounceTimer;
      let requestId = 0;

      async function fetchHeaderPredictiveResults(term) {
        const currentRequest = ++requestId;

        predictiveSearch.setAttribute('loading', 'true');
        predictiveSearch.setAttribute('open', 'true');
        input.setAttribute('aria-expanded', 'true');

        try {
          const response = await originalFetch(buildPredictiveSearchUrl(term));
          if (!response.ok) throw new Error(String(response.status));

          const html = await response.text();
          if (currentRequest !== requestId) return;

          renderPredictiveMarkup(predictiveSearch, resultsContainer, html);
          updateSearchForDisplay(predictiveSearch, term);
        } catch (error) {
          if (currentRequest !== requestId) return;
          console.error('Header predictive search error:', error);
          showPredictiveFallback(predictiveSearch, resultsContainer, term);
        }
      }

      function handleHeaderSearchInput() {
        clearTimeout(debounceTimer);
        const term = input.value.trim();

        if (!term) {
          requestId += 1;
          predictiveSearch.removeAttribute('open');
          predictiveSearch.removeAttribute('loading');
          predictiveSearch.removeAttribute('results');
          resultsContainer.innerHTML = getLoadingMarkup();
          input.setAttribute('aria-expanded', 'false');
          return;
        }

        debounceTimer = setTimeout(() => {
          if (input.value.trim() !== term) return;
          fetchHeaderPredictiveResults(term);
        }, DEBOUNCE_MS);
      }

      input.addEventListener(
        'input',
        (event) => {
          event.stopImmediatePropagation();
          handleHeaderSearchInput();
        },
        true
      );

      input.addEventListener(
        'focus',
        (event) => {
          const term = input.value.trim();
          if (!term) return;
          event.stopImmediatePropagation();
          handleHeaderSearchInput();
        },
        true
      );
    });
  }

  function init() {
    bindSearchForms();
    normalizeSearchResultsUrl();
    initHeaderModalPredictiveSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
